import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Spinner } from '@/components/shared/UIComponents';
import { AuthFooter } from '@/components/auth/AuthFooter';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email'); return; }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo.svg" alt="DiscoveryGem" className="w-9 h-9 dark:brightness-0 dark:invert" />
          <div>
            <div className="font-semibold text-surface-900">DiscoveryGem</div>
            <div className="text-[10px] text-surface-500 uppercase tracking-widest">Platform</div>
          </div>
        </div>

        {sent ? (
          <div>
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-emerald-600 dark:text-emerald-400" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-surface-900 mb-2">Check your email</h1>
            <p className="text-sm text-surface-500 mb-6">
              If an account exists for <span className="text-surface-800 font-medium">{email}</span>,
              we've sent a password reset link.
            </p>
            <Link to="/auth/login" className="btn-secondary w-full justify-center">Back to Sign In</Link>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-surface-900 mb-2">Reset password</h1>
            <p className="text-sm text-surface-500 mb-8">Enter your email and we'll send a reset link</p>

            {error && (
              <div className="mb-6 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-sm text-red-600 dark:text-red-400">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="input-label">Email</label>
                <input type="email" className="input-field" placeholder="admin@company.com"
                  value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full py-3">
                {loading ? <Spinner size="sm" /> : 'Send Reset Link'}
              </button>
            </form>

            <p className="text-sm text-surface-500 text-center mt-8">
              <Link to="/auth/login" className="text-brand-600 hover:text-brand-700 font-medium transition-colors">
                Back to Sign In
              </Link>
            </p>
          </div>
        )}
      </div>
      </div>
      <AuthFooter />
    </div>
  );
}
